import { createSelector } from '@reduxjs/toolkit';
import type { SerializedEditorState } from 'lexical';

import type { CommentablePersonProfile } from 'js/src/client';
import type { NumOpenCommentsPerScene } from 'js/src/data/comments/comments-types';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';
import { type DeepReadonly, withStableEmptyArray } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';

const EMPTY_LIST: readonly number[] = [];

export const doesSceneHaveComments = (sceneId: string) => (state: RootState) => {
    return getCommentIdsBySceneId(sceneId)(state).length > 0;
};

export const getCommentDraftBySlideId =
    (slideId: string) =>
    (state: RootState): SerializedEditorState | null =>
        state.comments.comment_drafts[slideId] ?? null;

export const getNumOpenCommentsPerScene = createSelector(
    [(state: RootState) => state.comments.scene_comments],
    (sceneComments): NumOpenCommentsPerScene => {
        const orm = new PlainJsStoreORM(sceneComments);
        const response: Record<string, number> = {};

        orm.forEach((sceneComment) => {
            const numOpenComments = sceneComment.open_comment_ids.length;
            const numResolvedComments = sceneComment.resolved_comment_ids.length;
            if (numOpenComments > 0 || numResolvedComments > 0) {
                response[sceneComment.slide_id] = numOpenComments;
            }
        });

        return response;
    },
);

export const doesVideoHaveComments = (state: RootState) => {
    return state.comments.comments.allIds.length > 0;
};

export const getCommentsUiState = (state: RootState) => {
    return state.comments.comments_ui;
};

export const getCommentsAreLoading = (state: RootState) => {
    const commentsUiState = getCommentsUiState(state);
    return commentsUiState.loadingComments;
};

const getSceneComment = (state: RootState, sceneId: string) =>
    new PlainJsStoreORM(state.comments.scene_comments).getById(sceneId);

/**
 *
 * @param {string} sceneId : the id of scene that contains all the comments
 * @returns ids of all the comments on that scene, EMPTY_LIST if empty
 */
export const getCommentIdsBySceneId = (sceneId: string) => (state: RootState) => {
    return withStableEmptyArray(getSceneComment(state, sceneId)?.comment_ids ?? EMPTY_LIST);
};

export const getOpenCommentIdsBySceneId = (sceneId: string) => (state: RootState) => {
    return withStableEmptyArray(getSceneComment(state, sceneId)?.open_comment_ids ?? EMPTY_LIST);
};

export const getResolvedCommentIdsBySceneId = (sceneId: string) => (state: RootState) => {
    return withStableEmptyArray(
        getSceneComment(state, sceneId)?.resolved_comment_ids ?? EMPTY_LIST,
    );
};

const getComment = (state: RootState, commentId: number) =>
    new PlainJsStoreORM(state.comments.comments).getByIdOrThrow(commentId);

export const getMessageByCommentId = (commentId: number) => (state: RootState) => {
    return getComment(state, commentId).message;
};

export const getLastUpdatedByCommentId = (commentId: number) => (state: RootState) => {
    return getComment(state, commentId).last_updated;
};

export const getUserByCommentId =
    (commentId: number) =>
    (state: RootState): DeepReadonly<CommentablePersonProfile> | null => {
        return getComment(state, commentId).user || null;
    };

export const getUserPhotoUrlByCommentId = (commentId: number) => (state: RootState) => {
    return getComment(state, commentId).user_photo_url;
};

export const getUserDisplayNameByCommentId = (commentId: number) => (state: RootState) => {
    return getComment(state, commentId).external_user_name;
};

export const getUserDisplayEmailByCommentId = (commentId: number) => (state: RootState) => {
    return getComment(state, commentId).external_user_email;
};

export const getUsersFromCommentThread = createSelector(
    (state: RootState) => state.comments.comments_users,
    (comments_users): readonly DeepReadonly<CommentablePersonProfile>[] => {
        const orm = new PlainJsStoreORM(comments_users);
        return orm.map((nonTeamMemberInfo) => {
            const fullName = nonTeamMemberInfo.external_user_name;
            const email = nonTeamMemberInfo.external_user_email;
            const splitIndex = fullName.indexOf(' ');
            const first_name = fullName.slice(0, splitIndex);
            const last_name = fullName.slice(splitIndex + 1);
            return {
                first_name,
                last_name,
                email,
                id: null,
            };
        });
    },
);
